import type { FC, ReactNode } from 'react'
import type { GaltonControls, LandEvent } from '@/components/landing/galton-board'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Plus, ShieldCheck, Volume2, VolumeX } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatNumber } from '@/lib/format'
import { createRandom, randomHex } from '@/lib/random'
import { unlockAudio } from '@/lib/synth'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import GaltonBoard, { formatBin } from '@/components/landing/galton-board'
import { PLINKO_12, PLINKO_16, RTP_OPTIONS } from '@/constants/kinematic'

type LedgerEntry = {
    id: number
    multiplier: number
    hash: string
}

type Stats = {
    count: number
    total: number
    last: number | null
}

const EMPTY_STATS: Stats = { count: 0, total: 0, last: null }

const seedLedger = (): LedgerEntry[] => {
    const random = createRandom(2026)

    return Array.from({ length: 6 }, (_, index) => ({
        id: 41230 - index,
        multiplier: PLINKO_16[4 + Math.floor(random() * 9)],
        hash: `${randomHex(6, random)}…${randomHex(4, random)}`
    }))
}

const INITIAL_LEDGER = seedLedger()

const hudButton = 'inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 font-mono text-[10px] uppercase tracking-[0.12em] text-steel transition-colors hover:bg-white/[0.06] hover:text-bone'

const Label: FC<{ children: ReactNode }> = ({ children }) => {
    return (
        <span className='font-mono text-[10px] uppercase tracking-[0.16em] text-steel'>
            {children}
        </span>
    )
}

const HeroStage: FC = () => {
    const reduced = useReducedMotion()
    const { ref, inView } = useInView<HTMLDivElement>('120px')
    const [rows, setRows] = useState(16)
    const [rtp, setRtp] = useState(99)
    const [sound, setSound] = useState(false)
    const [ledger, setLedger] = useState(INITIAL_LEDGER)
    const [stats, setStats] = useState<Stats>(EMPTY_STATS)
    const soundRef = useRef(false)
    const controlsRef = useRef<GaltonControls | null>(null)
    const nextId = useRef(41231)

    useEffect(() => {
        const query = window.matchMedia('(max-width: 639px)')
        const update = () => setRows(query.matches ? 12 : 16)

        update()
        query.addEventListener('change', update)

        return () => query.removeEventListener('change', update)
    }, [])

    useEffect(() => {
        setStats(EMPTY_STATS)
    }, [rtp, rows])

    const multipliers = useMemo(() => {
        const table = rows === 12 ? PLINKO_12 : PLINKO_16
        return table.map(value => (value * rtp) / 99)
    }, [rows, rtp])

    const handleLand = useCallback((event: LandEvent) => {
        const id = nextId.current
        nextId.current += 1

        setLedger(previous => [{ id, multiplier: event.multiplier, hash: `${randomHex(6)}…${randomHex(4)}` }, ...previous].slice(0, 6))
        setStats(previous => ({ count: previous.count + 1, total: previous.total + event.multiplier, last: event.multiplier }))
    }, [])

    const toggleSound = () => {
        const next = !sound
        if (next) unlockAudio()
        soundRef.current = next
        setSound(next)
    }

    const observed = stats.count > 0 ? (stats.total / stats.count) * 100 : null
    const observedLabel = observed === null ? 'Calibrating' : `${observed.toFixed(2)}%`
    const lastLabel = stats.last === null ? 'Waiting' : `×${formatBin(stats.last)}`

    return (
        <div
            ref={ref}
            className='relative overflow-hidden rounded-[30px] border border-white/10 bg-[#09090b]/90 shadow-[0_70px_160px_-60px_rgba(255,91,31,0.5)] backdrop-blur'
        >
            <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent' />

            <div className='flex h-12 items-center justify-between gap-3 border-b border-white/[0.06] pl-4 pr-2 md:pl-5'>
                <div className='flex min-w-0 items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.14em] text-steel md:text-[11px]'>
                    <span aria-hidden='true' className='size-1.5 shrink-0 animate-pulse-dot rounded-full bg-ember motion-reduce:animate-none' />
                    <span className='text-bone'>Plinko</span>
                    <span>{rows} rows</span>
                    <span className='hidden sm:inline'>Medium risk</span>
                </div>

                <span className='hidden font-mono text-[10px] uppercase tracking-[0.14em] text-steel/80 md:block'>
                    Demo rounds, no real money
                </span>

                <div className='flex items-center gap-0.5'>
                    <button type='button' onClick={toggleSound} aria-pressed={sound} className={cn(hudButton, sound && 'text-ember')}>
                        {sound ? <Volume2 aria-hidden='true' className='size-3.5' /> : <VolumeX aria-hidden='true' className='size-3.5' />}
                        <span className='hidden sm:inline'>Sound</span>
                        <span className='sr-only sm:hidden'>Sound</span>
                    </button>

                    <button type='button' onClick={() => controlsRef.current?.drop(10)} className={hudButton}>
                        <Plus aria-hidden='true' className='size-3.5' />
                        Drop 10
                    </button>
                </div>
            </div>

            <div className='grid lg:grid-cols-[250px_minmax(0,1fr)_250px]'>
                <div className='hidden flex-col border-r border-white/[0.06] p-5 lg:flex'>
                    <div className='flex items-center justify-between'>
                        <Label>Settled bets</Label>
                        <span className='flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.14em] text-mint'>
                            <ShieldCheck aria-hidden='true' className='size-3.5' />
                            Verified
                        </span>
                    </div>

                    <ul className='mt-4 grid gap-1.5'>
                        {ledger.map(entry => (
                            <li
                                key={entry.id}
                                className='grid animate-ledger-in grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2.5 rounded-xl border border-white/[0.04] bg-white/[0.02] px-3 py-2.5 font-mono text-[10.5px] motion-reduce:animate-none'
                            >
                                <span className='text-steel'>#{entry.id}</span>
                                <span className='truncate text-steel/55'>{entry.hash}</span>
                                <span className={entry.multiplier >= 1 ? 'text-ember' : 'text-bone/70'}>
                                    ×{entry.multiplier.toFixed(2)}
                                </span>
                            </li>
                        ))}
                    </ul>

                    <p className='mt-auto pt-5 text-xs leading-relaxed text-steel'>
                        Every row carries a proof your players can check in the browser.
                    </p>
                </div>

                <div className='relative h-[380px] sm:h-[460px] lg:h-[540px]'>
                    <div aria-hidden='true' className='pointer-events-none absolute inset-x-[12%] bottom-0 h-1/2 rounded-full bg-ember/[0.07] blur-3xl' />

                    <GaltonBoard
                        rows={rows}
                        multipliers={multipliers}
                        interval={230}
                        playing={inView && !reduced}
                        histogram
                        soundRef={soundRef}
                        controlsRef={controlsRef}
                        onLand={handleLand}
                        label='A live Plinko board where balls fall through rows of pegs into multiplier bins'
                        className='absolute inset-0 cursor-pointer'
                    />
                </div>

                <div className='hidden flex-col gap-7 border-l border-white/[0.06] p-5 lg:flex'>
                    <div>
                        <Label>Observed return</Label>
                        <div className={cn('mt-2.5 font-mono leading-none tracking-tight text-bone', observed === null ? 'text-lg text-steel' : 'text-[30px]')}>
                            {observedLabel}
                        </div>
                        <div className='mt-2.5 font-mono text-[11px] text-steel'>
                            Expected {rtp.toFixed(2)}%, {formatNumber(stats.count)} balls
                        </div>
                    </div>

                    <div>
                        <Label>Last landing</Label>
                        <div className={cn('mt-2.5 font-mono leading-none', stats.last === null ? 'text-lg text-steel' : 'text-[30px] text-ember')}>
                            {lastLabel}
                        </div>
                    </div>

                    <div className='mt-auto'>
                        <Label>Return to player</Label>

                        <div role='radiogroup' aria-label='Return to player' className='mt-3 grid grid-cols-4 gap-1 rounded-xl border border-white/[0.07] bg-white/[0.02] p-1'>
                            {RTP_OPTIONS.map(option => (
                                <button
                                    key={option}
                                    type='button'
                                    role='radio'
                                    aria-checked={rtp === option}
                                    onClick={() => setRtp(option)}
                                    className={cn('h-8 rounded-lg font-mono text-[11px] transition-colors', rtp === option ? 'bg-bone text-ink' : 'text-steel hover:text-bone')}
                                >
                                    {option}%
                                </button>
                            ))}
                        </div>

                        <p className='mt-3 text-xs leading-relaxed text-steel'>
                            Pick 99, 98, 97 or 96% per casino. Watch the bins rescale.
                        </p>
                    </div>
                </div>
            </div>

            <div className='grid grid-cols-3 divide-x divide-white/[0.06] border-t border-white/[0.06] lg:hidden'>
                <div className='p-4'>
                    <Label>Return</Label>
                    <div className='mt-1.5 truncate font-mono text-sm text-bone'>{observedLabel}</div>
                </div>
                <div className='p-4'>
                    <Label>Balls</Label>
                    <div className='mt-1.5 font-mono text-sm text-bone'>{formatNumber(stats.count)}</div>
                </div>
                <div className='p-4'>
                    <Label>Last</Label>
                    <div className='mt-1.5 truncate font-mono text-sm text-ember'>{lastLabel}</div>
                </div>
            </div>

            <div className='flex flex-col gap-1.5 border-t border-white/[0.06] px-4 py-3.5 font-mono text-[10px] uppercase tracking-[0.14em] text-steel md:flex-row md:items-center md:justify-between md:px-5'>
                <span>Each path is fixed before the ball drops and checked after it lands</span>
                <span className='text-steel/70'>Tap the board to drop more</span>
            </div>
        </div>
    )
}

export default HeroStage
